import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DetailSkeleton } from "@/components/Skeletons";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Award, ClipboardCheck, ClipboardList, Clock, FileWarning, GraduationCap, IndianRupee, TrendingUp, UserCheck, Users } from "lucide-react";
import DateRangeFilter from "@/components/DateRangeFilter";
import MultiSelect from "@/components/MultiSelect";
import FilterPills from "@/components/FilterPills";
import { presetRange, rangeLabel, matchingPreset } from "@/lib/dates";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))",
                "hsl(var(--chart-4))", "hsl(var(--chart-5))"];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// How many units the by-unit chart draws before it stops. A dashboard is a summary, and
// growing a card one row per unit with no bound is how this one came to be taller than
// the page — which also stretched the two cards beside it, because a grid row is as tall
// as its tallest cell, and they rendered as empty boxes with axes and no bars.
// The busiest are the ones worth seeing; the rest are a line of text and the register.
const UNITS_CHARTED = 8;
const DEPTS_CHARTED = 10;

// The dashboard's filters are the register's, under the register's names, so a tile or
// a bar can open the register by appending one more key to the string it already holds.
const MULTI = { units: "unit_id", depts: "department_id" };

const deptsUnder = (depts, unitIds) =>
  unitIds.length === 0 ? depts : depts.filter((d) => unitIds.includes(d.unit_id));

const monthLabel = (key) => {
  const [y, m] = String(key).split("-").map(Number);
  return m ? `${MONTHS_SHORT[m - 1]} ${String(y).slice(2)}` : key;
};
const monthBounds = (key) => {
  const [y, m] = String(key).split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return { from: `${key}-01`, to: `${key}-${String(last).padStart(2, "0")}` };
};

const tooltipStyle = { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" };
// A tooltip row is coloured by its series — a bar's fill, a line's stroke — and Recharts falls back
// to a hardcoded black for a row that has none. A pie's rows never have one: its slice colours live
// on <Cell>, which the tooltip does not read. So on the dark theme's popover "Internal : 3" was black
// on near-black. Rows with no series colour take the theme's popover text instead.
const tooltipItemText = { color: "hsl(var(--popover-foreground))" };
// Recharts hovers a bar chart by painting a slab BEHIND the bar (a hardcoded `#ccc`
// rectangle, which on the dark theme reads as a white block lying across the chart) and
// outlining the bar in its own default stroke. A hover should point at the mark, not
// wash the plot: the cursor is off, and the bar you are on brightens instead.
const lineCursor = { stroke: "hsl(var(--muted-foreground))", strokeWidth: 1 };
// `fillOpacity` keeps the series colour — a bar stays recognisably its own series while
// it is the one being read — and reads as a lift against the card in both themes.
const activeBar = { fillOpacity: 0.7 };

/**
 * A dashboard tile is a door or it is a caption.
 *
 * A tile whose label names a slice of a list — "Scheduled", "Employees trained" — takes
 * `to` and opens that list already filtered to the same period and units as the tile.
 * A tile that is a sum or a ratio — total hours, attendance % — has nothing to open and
 * stays inert, with a note here saying why.
 */
function KPI({ icon: Icon, label, value, hint, testid, to }) {
  const nav = useNavigate();
  const inner = (
    <div className="flex items-start justify-between w-full">
      <div className="text-left min-w-0">
        <div className="overline text-muted-foreground">{label}</div>
        <div className="font-heading font-bold text-2xl mt-2">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </div>
      <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" aria-hidden="true" />
      </div>
    </div>
  );
  if (!to) return <Card className="p-5 border border-border bg-card" data-testid={testid}>{inner}</Card>;
  return (
    <Card className="kpi-card p-5 border border-border bg-card overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => nav(to)} className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
        {inner}
        <span className="sr-only">Open the matching records</span>
      </button>
    </Card>
  );
}

function ChartCard({ overline, title, note, testid, children }) {
  return (
    <Card className="p-5 border border-border" data-testid={testid}>
      <div className="overline text-muted-foreground">{overline}</div>
      <h3 className="font-heading font-semibold mt-1 mb-4">
        {title}
        {note && <span className="ml-2 font-normal text-xs text-muted-foreground">{note}</span>}
      </h3>
      {children}
    </Card>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [stats, setStats] = useState(null);
  // The query string `stats` answers, so figures are drawn only under the filters they
  // belong to; and what went wrong when the latest request did not answer at all.
  const [statsFor, setStatsFor] = useState(null);
  const [statsError, setStatsError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [units, setUnits] = useState([]);
  const [depts, setDepts] = useState([]);
  const [mastersLoaded, setMastersLoaded] = useState(false);
  const [mastersError, setMastersError] = useState(false);

  const f = useMemo(() => ({
    date_from: sp.get("date_from") || "",
    date_to: sp.get("date_to") || "",
    units: sp.getAll("unit_id"),
    depts: sp.getAll("department_id"),
  }), [sp]);

  const write = useCallback((patch) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      for (const [field, value] of Object.entries(patch)) {
        const key = MULTI[field] || field;
        next.delete(key);
        for (const v of (Array.isArray(value) ? value : [value])) if (v) next.append(key, v);
      }
      return next;
    }, { replace: true });
  }, [setSp]);

  // The period is always in the URL, so what the numbers cover is never implicit. The
  // default is the current financial year — the year an auditor at Shakun means.
  useEffect(() => {
    if (!f.date_from && !f.date_to) {
      const fy = presetRange("this_fy");
      write({ date_from: fy.from, date_to: fy.to });
    }
  }, [f.date_from, f.date_to, write]);

  const search = sp.toString();
  useEffect(() => {
    if (!f.date_from && !f.date_to) return undefined;
    let live = true;
    setStatsError("");
    api.get(`/dashboard/stats?${search}`)
      .then((r) => {
        if (!live) return;
        if (!r.data || typeof r.data !== "object" || Array.isArray(r.data)) throw new Error("unexpected response");
        setStats(r.data);
        setStatsFor(search);
      })
      // Swallowed, a failure left the previous query's figures standing under the new
      // period's heading (another period's zero on the two tiles that should be zero),
      // and a failed first load spun the skeleton forever.
      .catch((e) => {
        if (live) setStatsError(formatApiError(e.response?.data?.detail, "The figures for this period could not be loaded."));
      });
    return () => { live = false; };
  }, [search, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    Promise.all([api.get("/units"), api.get("/departments")])
      .then(([u, d]) => {
        if (!Array.isArray(u.data) || !Array.isArray(d.data)) throw new Error("unexpected response");
        setUnits(u.data); setDepts(d.data); setMastersLoaded(true);
      })
      // Swallowed, the pickers said the company had no units or departments on record
      // while the charts below grouped trainings by exactly those.
      .catch(() => setMastersError(true));
  }, []);

  // Pruned only against departments that loaded. Against an empty list, choosing a unit
  // silently dropped every department already in the filter.
  const setUnitsFor = (ids) => write({
    units: ids,
    depts: mastersLoaded ? f.depts.filter((did) => deptsUnder(depts, ids).some((d) => d.id === did)) : f.depts,
  });

  // Every door carries the dashboard's own filters, then one more key.
  const door = (extra = {}) => {
    const p = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(extra)) {
      p.delete(k);
      for (const x of (Array.isArray(v) ? v : [v])) if (x) p.append(k, x);
    }
    return `/trainings?${p.toString()}`;
  };
  // The same filters, pointed at Reports. Coverage stays inert as a NUMBER — a ratio is a
  // caption, not a door — but its inverse IS a list, and that list had no screen at all
  // until Reports got one.
  const reportsDoor = () => `/reports?${new URLSearchParams(sp).toString()}`;
  const open = (extra) => nav(door(extra));

  const unitOptions = units.map((u) => ({ id: u.id, name: u.name, hint: u.location }));
  const deptOptions = deptsUnder(depts, f.units).map((d) => ({
    id: d.id, name: d.name, hint: units.find((u) => u.id === d.unit_id)?.name }));
  // A pill whose list never arrived says so. The raw id stood in for the name, and "Unit:
  // 3f6c…" tells nobody what the figures are narrowed to.
  const nameOf = (list, id) => list.find((x) => x.id === id)?.name || (mastersError ? "(name not loaded)" : id);
  const pills = [];
  f.units.forEach((id) => pills.push({ key: `u-${id}`, label: "Unit", value: nameOf(units, id),
    onRemove: () => setUnitsFor(f.units.filter((x) => x !== id)) }));
  f.depts.forEach((id) => pills.push({ key: `d-${id}`, label: "Department", value: nameOf(depts, id),
    onRemove: () => write({ depts: f.depts.filter((x) => x !== id) }) }));

  const preset = matchingPreset({ from: f.date_from, to: f.date_to });
  const periodLabel = preset ? preset.label.toLowerCase() : rangeLabel({ from: f.date_from, to: f.date_to });

  // Figures are drawn only when they answer the filters on screen. A request that failed,
  // or is still out for new filters, used to leave the previous query's numbers standing
  // under the new heading, so another period's zero on "Signed sheets missing" read as
  // this period's.
  const ready = stats !== null && statsFor === search;

  // The heading and the filters are the question, so they stand whatever the answer is
  // doing: while it loads, and when it cannot, the filters can still be changed.
  const header = (
    <>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">Overview</div>
          <h2 className="font-heading font-bold text-3xl mt-1">Training Insights</h2>
          <p className="text-sm text-muted-foreground mt-1" data-testid="dashboard-period">
            {periodLabel ? `Learning activity ${preset ? "for" : ""} ${periodLabel}` : "Learning activity"}
            {f.units.length || f.depts.length ? ", narrowed below" : " across every unit and department"}.
            Every tile and bar opens the register on the rows it counts.
          </p>
        </div>
      </div>

      <Card className="p-4 border border-border space-y-3" data-testid="dashboard-filters">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <DateRangeFilter value={{ from: f.date_from, to: f.date_to }}
                           onChange={({ from, to }) => write({ date_from: from, date_to: to })}
                           placeholder="This financial year" testid="dash-dates" />
          <MultiSelect value={f.units} onChange={setUnitsFor} options={unitOptions}
                       placeholder="Every unit" noun="unit" searchPlaceholder="Search units..."
                       empty={mastersError ? "Could not load units — reload to try again." : "No units on record yet."}
                       testid="dash-unit" />
          <MultiSelect value={f.depts} onChange={(v) => write({ depts: v })} options={deptOptions}
                       placeholder={f.units.length ? "Any of their departments" : "People from any department"}
                       noun="department" searchPlaceholder="Search departments..."
                       empty={mastersError ? "Could not load departments — reload to try again."
                         : f.units.length ? "The selected units have no departments on record." : "No departments on record yet."}
                       testid="dash-department" />
        </div>
        <FilterPills pills={pills} count={ready ? stats.total_trainings : "—"} noun="training"
                     onClearAll={() => write({ units: [], depts: [] })} />
      </Card>
    </>
  );

  if (!ready) {
    return (
      <div className="space-y-6" data-testid="dashboard-page">
        {header}
        {statsError ? (
          <Card className="p-8 border border-border text-center" data-testid="dashboard-error">
            <p className="text-sm text-destructive-text">{statsError}</p>
            <Button variant="outline" className="mt-4" onClick={() => setAttempt((n) => n + 1)}
                    data-testid="dashboard-retry">Try again</Button>
          </Card>
        ) : <DetailSkeleton testid="dashboard-skeleton" />}
      </div>
    );
  }

  const allUnits = [...(stats.by_unit || [])].sort((a, b) => (b.count || 0) - (a.count || 0));
  const chartedUnits = allUnits.slice(0, UNITS_CHARTED);
  const hiddenUnits = allUnits.length - chartedUnits.length;
  const chartedDepts = (stats.by_department || []).slice(0, DEPTS_CHARTED);
  const hiddenDepts = (stats.by_department || []).length - chartedDepts.length;
  const perMonth = (stats.per_month || []).map((p) => ({ ...p, name: monthLabel(p.month) }));
  const pie = [{ name: "Internal", value: stats.internal, key: "INTERNAL" }, { name: "External", value: stats.external, key: "EXTERNAL" }];
  const clickable = "cursor-pointer";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {header}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KPI icon={GraduationCap} label="Trainings" value={stats.total_trainings}
             hint={`${stats.completed_trainings} delivered`} testid="kpi-total-trainings" to={door()} />
        {/* The one figure here that should be zero: delivered sessions whose marks have no
            signed paper behind them. A door, straight to the rows that need a scan. */}
        <KPI icon={FileWarning} label="Signed sheets missing" value={stats.sheets_missing}
             hint={stats.sheets_missing ? "Delivered sessions with no scan on file" : "Every delivered session has its sheet"}
             testid="kpi-sheets-missing" to={door({ status: "Completed", attendance_sheet: "missing" })} />
        {/* The second figure that should be zero. ISO 9001/14001/45001 §7.2 all ask
            whether the training WORKED — a delivered session where nobody who attended
            has been evaluated is the gap an auditor finds. Inert rather than a door:
            there is no `evaluation=missing` filter on the register yet, and a tile that
            opened an unfiltered list would be worse than one that opens nothing. */}
        <KPI icon={ClipboardCheck} label="Evaluations missing" value={stats.evaluations_missing}
             hint={stats.evaluations_missing
               ? "Delivered sessions nobody has evaluated yet"
               : "Every delivered session has been evaluated"}
             testid="kpi-evaluations-missing" />
        {/* A sum of hours. There is no list of hours to open. */}
        <KPI icon={Clock} label="Training hours" value={stats.total_hours} hint="Delivered sessions only" testid="kpi-total-hours" />
        <KPI icon={Users} label="Employees trained" value={stats.unique_employees}
             hint="Marked present at a delivered session, leavers included" testid="kpi-employees"
             to={door({ attendance_status: "Present", status: "Completed" })} />
        {/* Coverage is about absence: the people who had nothing. A ratio, not a slice.
            It counts people STILL ON THE REGISTER on both sides of the fraction — the tile
            above counts everyone who attended, leavers included, because that is what was
            delivered and it stays true after somebody resigns. Reading one number off each
            population is why this said "4 of 3 people trained", so each now names the
            population it is about rather than leaving the two looking contradictory. */}
        <KPI icon={UserCheck} label="Coverage" value={`${stats.coverage_pct}%`}
             testid="kpi-coverage"
             hint={<>
               {stats.covered_employees} of {stats.headcount} current employees trained
               {stats.headcount > stats.covered_employees && (
                 <> · <Link to={reportsDoor()} className="underline underline-offset-2 hover:text-foreground"
                            data-testid="kpi-coverage-who">see who hasn't</Link></>
               )}
             </>} />
        <KPI icon={ClipboardList} label="Scheduled" value={stats.upcoming}
             testid="kpi-upcoming" to={door({ status: "Scheduled" })} />
        <KPI icon={Award} label="Certificates issued" value={stats.certificates_issued}
             testid="kpi-certificates" to="/certificates" />
        {/* A ratio across the period — not a slice anything can be filtered to. */}
        <KPI icon={TrendingUp} label="Attendance" value={`${stats.attendance_pct}%`}
             hint="Of marked seats, present" testid="kpi-attendance" />
        <KPI icon={IndianRupee} label="Spend" value={`₹${(stats.total_spend || 0).toLocaleString("en-IN")}`}
             hint="Actual where recorded, else estimated" testid="kpi-spend" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ChartCard overline="Trainings per month" title="Monthly trend" note="click a month" testid="chart-per-month">
            <ResponsiveContainer width="100%" height={260} className={clickable}>
              <LineChart data={perMonth} onClick={(e) => { const m = e?.activePayload?.[0]?.payload?.month; if (m) open(monthBounds(m)); }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={lineCursor} />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 3 }} name="Trainings" />
                <Line type="monotone" dataKey="hours" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={{ r: 3 }} name="Hours delivered" />
                <Legend />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard overline="Internal vs external" title="Trainer mix" note="click a slice" testid="chart-int-ext">
          <ResponsiveContainer width="100%" height={260} className={clickable}>
            <PieChart>
              <Pie data={pie} innerRadius={50} outerRadius={90} dataKey="value" label
                   onClick={(d) => d?.key && open({ training_type: d.key })}>
                {pie.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Legend />
              <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemText} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard overline="By unit" title="Trainings & hours" testid="chart-by-unit"
                   note={hiddenUnits > 0 ? `busiest ${UNITS_CHARTED} of ${allUnits.length} · click a bar` : "click a bar"}>
          {/* Horizontal, like the others down the side: a plant's name has the full width
              of the card and the bars still read left to right. */}
          <ResponsiveContainer width="100%" height={Math.max(240, chartedUnits.length * 44)} className={clickable}>
            <BarChart data={chartedUnits} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
              <YAxis type="category" dataKey="unit" stroke="hsl(var(--muted-foreground))" fontSize={11} width={130} interval={0}
                     tickFormatter={(v) => (String(v).length > 20 ? `${String(v).slice(0, 19)}…` : v)} />
              <Tooltip contentStyle={tooltipStyle} cursor={false} />
              <Legend />
              <Bar dataKey="count" fill="hsl(var(--chart-1))" activeBar={activeBar} name="Trainings" radius={[0, 3, 3, 0]}
                   onClick={(d) => d?.unit_id && open({ unit_id: d.unit_id })} />
              <Bar dataKey="hours" fill="hsl(var(--chart-3))" activeBar={activeBar} name="Hours" radius={[0, 3, 3, 0]}
                   onClick={(d) => d?.unit_id && open({ unit_id: d.unit_id, status: "Completed" })} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard overline="By department" title="Person-hours received" testid="chart-by-department"
                   note={hiddenDepts > 0 ? `top ${DEPTS_CHARTED} of ${stats.by_department.length} · click a bar` : "click a bar"}>
          {/* The attendee's department: "Production received 42 hours" means people from
              Production sat through them. A bar opens the register on the sessions those
              people were present at. */}
          {chartedDepts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Nobody was marked present at a delivered training in this period.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(240, chartedDepts.length * 40)} className={clickable}>
              <BarChart data={chartedDepts} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                <YAxis type="category" dataKey="department" stroke="hsl(var(--muted-foreground))" fontSize={11} width={110} interval={0}
                       tickFormatter={(v) => (String(v).length > 16 ? `${String(v).slice(0, 15)}…` : v)} />
                <Tooltip contentStyle={tooltipStyle} cursor={false}
                         formatter={(value, name, item) => name === "Hours" ? [`${value} hrs · ${item.payload.people} people · ${item.payload.trainings} sessions`, item.payload.unit || "Hours"] : [value, name]} />
                <Bar dataKey="hours" fill="hsl(var(--chart-2))" activeBar={activeBar} name="Hours" radius={[0, 3, 3, 0]}
                     onClick={(d) => d?.department_id && open({ department_id: d.department_id, attendance_status: "Present", status: "Completed" })} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard overline="By category" title="Topic distribution" note="click a bar" testid="chart-by-category">
          <ResponsiveContainer width="100%" height={240} className={clickable}>
            <BarChart data={stats.by_category}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="category" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={false} />
              <Bar dataKey="count" fill="hsl(var(--chart-2))" activeBar={activeBar} name="Trainings"
                   onClick={(d) => d?.category && open({ category: d.category })} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard overline="Top employees" title="By training hours" note="click a bar" testid="chart-top-employees">
          {(stats.top_employees || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">No delivered hours in this period.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240} className={clickable}>
              <BarChart data={stats.top_employees} layout="vertical">
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} width={100} />
                <Tooltip contentStyle={tooltipStyle} cursor={false} />
                <Bar dataKey="hours" fill="hsl(var(--chart-4))" activeBar={activeBar} name="Hours"
                     onClick={(d) => d?.employee_id && open({ employee_id: d.employee_id })} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
